if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();
      this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
      this.onClickRefreshList = this.onClickRefreshList.bind(this);
      this.variantId = null;
      this.variantAvailable = false;
      this.abortController = null;
      this.drawer = null;
    }

    connectedCallback() {
      this.errorHtml ||= this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
      if (this.variantAvailable || this.hasAttribute('available')) {
        this.update({ id: this.variantId || this.dataset.variantId, available: true });
      }
    }

    disconnectedCallback() {
      this.cancelRequest();
      this.innerHTML = '';
      this.removeAttribute('available');
      this.removeDrawer();
    }

    cancelRequest() {
      this.abortController?.abort();
      this.abortController = null;
    }

    removeDrawer() {
      const drawer = this.drawer;
      if (!drawer) return;
      drawer.hide(false);
      drawer.remove();
      this.drawer = null;
    }

    update(variant) {
      this.cancelRequest();
      this.innerHTML = '';
      this.removeAttribute('available');
      this.removeDrawer();
      this.variantId = variant?.id && variant.available ? String(variant.id) : null;
      this.variantAvailable = Boolean(this.variantId);
      if (!this.variantAvailable) {
        delete this.dataset.variantId;
        return;
      }
      this.dataset.variantId = this.variantId;
      this.fetchAvailability(this.variantId);
    }

    fetchAvailability(variantId) {
      if (!this.isConnected || !this.variantAvailable || String(variantId) !== this.variantId) return;
      this.cancelRequest();
      const controller = new AbortController();
      this.abortController = controller;
      const isCurrent = () => this.isConnected && controller === this.abortController && this.variantAvailable && this.variantId === String(variantId);
      const requestTimeout = setTimeout(() => {
        if (!isCurrent()) return;
        controller.abort();
        this.renderError();
      }, 12000);
      const clearRequestTimeout = () => clearTimeout(requestTimeout);
      controller.signal.addEventListener('abort', clearRequestTimeout, { once: true });
      let rootUrl = this.dataset.rootUrl;
      if (!rootUrl.endsWith('/')) rootUrl += '/';
      const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

      fetch(variantSectionUrl, { signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error('Pickup availability could not be loaded.');
          return response.text();
        })
        .then(text => {
          if (!isCurrent() || controller.signal.aborted) return;
          const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
          if (!sectionInnerHTML) throw new Error('Pickup availability response was incomplete.');
          this.renderPreview(sectionInnerHTML);
        })
        .catch(error => {
          if (!isCurrent() || controller.signal.aborted || error.name === 'AbortError') return;
          this.renderError();
        })
        .finally(() => {
          clearRequestTimeout();
          controller.signal.removeEventListener('abort', clearRequestTimeout);
        });
    }

    onClickRefreshList() {
      if (this.variantAvailable) this.fetchAvailability(this.variantId);
    }

    renderError() {
      this.removeDrawer();
      this.innerHTML = '';
      this.removeAttribute('available');
      if (!this.errorHtml) return;
      this.appendChild(this.errorHtml.cloneNode(true));
      this.querySelector('button')?.addEventListener('click', this.onClickRefreshList);
    }

    renderPreview(sectionInnerHTML) {
      this.removeDrawer();
      const preview = sectionInnerHTML.querySelector('pickup-availability-preview');
      this.innerHTML = '';
      this.removeAttribute('available');
      if (!preview) return;
      window.OTFSectionStyles?.retain(sectionInnerHTML);
      this.innerHTML = preview.outerHTML;
      this.setAttribute('available', '');
      this.drawer = sectionInnerHTML.querySelector('pickup-availability-popup');
      if (!this.drawer) return;
      document.body.appendChild(this.drawer);
      this.querySelector('button')?.addEventListener('click', event => {
        this.drawer?.show(event.target);
      });
    }
  });
}

if (!customElements.get('pickup-availability-popup')) {
  customElements.define('pickup-availability-popup', class PickupAvailabilityDrawer extends HTMLElement {
    constructor() {
      super();
      this.onKeyDown = event => {
        if (event.key !== 'Escape' || this.classList.contains('hide-popup')) return;
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      };
      this.onCloseClick = event => {
        if (!event.target.closest('.theme-popup-close')) return;
        event.preventDefault();
        event.stopPropagation();
        this.hide();
      };
      this.onTransitionEnd = event => {
        if (event.target === this) this.focusContents();
      };
    }

    connectedCallback() {
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('click', this.onCloseClick);
      this.addEventListener('transitionend', this.onTransitionEnd);
      this.visibilityObserver = new MutationObserver(() => this.syncVisibility());
      this.visibilityObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
      this.syncVisibility();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('click', this.onCloseClick);
      this.removeEventListener('transitionend', this.onTransitionEnd);
      this.hide(false);
    }

    otherPopupOpen() {
      return Array.from(document.querySelectorAll('.rt-theme-popup:not(.hide-popup), .overlay-navigation-panel__popup:not(.hide-popup)')).some(popup => popup !== this);
    }

    syncVisibility() {
      const open = !this.classList.contains('hide-popup');
      this.inert = !open;
      this.setAttribute('aria-hidden', String(!open));
      if (this.wasOpen && !open) this.releaseFocus(true);
      this.wasOpen = open;
    }

    focusContents() {
      if (!this.isConnected || this.classList.contains('hide-popup') || this.otherPopupOpen()) return;
      const active = document.activeElement;
      if (active !== document.body && active !== this.focusElement && !this.contains(active)) return;
      if (this.focusTrapHandler && this.focusTrapHandler !== trapFocusHandlers.focusin) return;
      if (this.focusTrapHandler && this.contains(active)) return;
      trapFocus(this, this.querySelector('.theme-popup-close') || this);
      this.focusTrapHandler = trapFocusHandlers.focusin;
    }

    releaseFocus(restore) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
      const ownsTrap = this.focusTrapHandler && this.focusTrapHandler === trapFocusHandlers.focusin;
      if (ownsTrap) removeTrapFocus();
      this.focusTrapHandler = null;
      const opener = this.focusElement;
      this.focusElement = null;
      const anotherPopup = this.otherPopupOpen() || document.body.hasAttribute('active-popup');
      if (this.openedOverlay && !anotherPopup) {
        document.querySelectorAll('.theme-popup-overlay').forEach(overlay => overlay.classList.add('hidden-overlay'));
      }
      this.openedOverlay = false;
      if (restore && ownsTrap && !anotherPopup && opener?.isConnected && !this.contains(opener) && (this.contains(document.activeElement) || document.activeElement === document.body)) {
        opener.focus({ preventScroll: true });
      }
    }

    show(focusElement) {
      cancelAnimationFrame(this.focusFrame);
      this.focusElement = focusElement;
      this.classList.remove('hide-popup');
      this.syncVisibility();
      document.querySelectorAll('.theme-popup-overlay').forEach(overlay => overlay.classList.remove('hidden-overlay'));
      this.openedOverlay = true;
      this.focusContents();
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = requestAnimationFrame(() => { this.focusFrame = null; this.focusContents(); });
      });
    }

    hide(restore = true) {
      this.classList.add('hide-popup');
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.wasOpen = false;
      this.releaseFocus(restore);
    }
  });
}

